import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Search, Loader2, AlertTriangle, Feather, ChevronLeft, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { PhotoAncien, ArchiveBadge, formatDateFr, formatTaille } from '../components/anciens-stagiaires/shared';
import { AncienStagiaireDialog } from '../components/anciens-stagiaires/AncienStagiaireDialog';

const LIMIT = 24;

const Bandeaux = ({ data, onRattrapage, onAllegerTout, busy }) => (
  <div className="space-y-2">
    {data.sans_archive > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="bandeau-sans-archive">
        <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {data.sans_archive} ancien(s) stagiaire(s) sans archive figée.</span>
        <Button size="sm" onClick={onRattrapage} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-rattrapage">
          Compléter les anciens dossiers
        </Button>
      </div>
    )}
    {data.a_alleger > 0 && (
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700" data-testid="bandeau-a-alleger">
        <span className="flex items-center gap-2"><Feather className="h-4 w-4" /> {data.a_alleger} dossier(s) archivé(s) peuvent être allégés.</span>
        <Button size="sm" variant="outline" onClick={onAllegerTout} disabled={busy} data-testid="btn-alleger-tout">Alléger tout</Button>
      </div>
    )}
  </div>
);

const CarteAncien = ({ item, onOpen }) => (
  <button type="button" onClick={() => onOpen(item.user_id)} className="text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#8DC63F] hover:shadow-md transition-[border-color,box-shadow] flex gap-4" data-testid={`ancien-card-${item.user_id}`}>
    <PhotoAncien userId={item.user_id} prenom={item.prenom} nom={item.nom} />
    <div className="min-w-0 space-y-1">
      <p className="font-semibold text-[#0F172A] truncate">{item.prenom} {item.nom?.toUpperCase()}</p>
      <p className="text-xs text-slate-500 truncate">{item.numero_candidat ? `N°${item.numero_candidat} · ` : ''}{item.centre_formation || '—'} · {item.annee_diplome || '—'}</p>
      <p className="text-xs text-slate-400">Moniteur depuis le {formatDateFr(item.date_conversion)}</p>
      <ArchiveBadge statut={item.archive_statut} allege={!!item.allege_le} />
    </div>
  </button>
);

export default function AnciensStagiaires() {
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [annee, setAnnee] = useState('');
  const [centre, setCentre] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmTout, setConfirmTout] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setRecherche(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const charger = useCallback(() => {
    axios.get('/anciens-stagiaires', { params: { q: recherche || undefined, annee: annee || undefined, centre: centre || undefined, page, limit: LIMIT } })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Impossible de charger les anciens stagiaires'));
  }, [recherche, annee, centre, page]);

  useEffect(() => { charger(); }, [charger]);

  const rattrapage = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post('/anciens-stagiaires/rattrapage');
      toast.success(`${r.profils_completes} fiche(s) complétée(s), ${r.archives_lancees} archive(s) en cours de création`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur pendant la mise à jour');
    } finally { setBusy(false); }
  };

  const allegerTout = async () => {
    setBusy(true);
    try {
      const { data: r } = await axios.post('/anciens-stagiaires/alleger-tout');
      toast.success(`${r.stagiaires_alleges} dossier(s) allégé(s) : ${formatTaille(r.octets_liberes)} libérés`);
      charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur pendant l'allègement");
    } finally { setBusy(false); setConfirmTout(false); }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const fermer = useCallback(() => setOuvert(null), []);

  return (
    <div className="space-y-6" data-testid="anciens-stagiaires-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A] flex items-center gap-3">
          <Archive className="h-8 w-8 text-[#8DC63F]" /> Anciens stagiaires
        </h1>
        <p className="text-slate-500 mt-1">Stagiaires passés Moniteur VNM : photo, prérequis de candidature et archive de fin de formation.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, prénom, email ou n° candidat…" className="pl-10" data-testid="anciens-search-input" />
        </div>
        <select value={annee} onChange={(e) => { setAnnee(e.target.value); setPage(1); }} className="h-10 px-3 text-sm border border-slate-200 rounded-md" data-testid="anciens-filtre-annee">
          <option value="">Toutes les années</option>
          {(data?.annees || []).map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={centre} onChange={(e) => { setCentre(e.target.value); setPage(1); }} className="h-10 px-3 text-sm border border-slate-200 rounded-md" data-testid="anciens-filtre-centre">
          <option value="">Tous les centres</option>
          {(data?.centres || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {data && <Bandeaux data={data} busy={busy} onRattrapage={rattrapage} onAllegerTout={() => setConfirmTout(true)} />}

      {!data ? (
        <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500" data-testid="anciens-empty">
          <Archive className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          Aucun ancien stagiaire trouvé
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-500" data-testid="anciens-total">{data.total} ancien(s) stagiaire(s)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.items.map((it) => <CarteAncien key={it.user_id} item={it} onOpen={setOuvert} />)}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="anciens-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-sm text-slate-600">Page {page} / {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="anciens-page-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </>
      )}

      {ouvert && <AncienStagiaireDialog userId={ouvert} onClose={fermer} onChanged={charger} />}

      <AlertDialog open={confirmTout} onOpenChange={setConfirmTout}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alléger {data?.a_alleger} dossier(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les fichiers de formation de ces anciens stagiaires seront retirés de la base de données. Ils restent dans leur archive ZIP figée. Photos, documents de prérequis et archives sont conservés. Action définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={allegerTout} data-testid="btn-alleger-tout-confirm">Alléger tout</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
